import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';

import { PostMeta } from './content/post.model';
import { BODIES } from './generated/bodies';
import { POSTS } from './generated/index';
import { currentLocale } from './site';

export interface ResolvedPost {
  meta: PostMeta;
  /** Rendered at build time. Never parsed in the browser. */
  html: string;
}

/**
 * The posts of this locale, newest first.
 *
 * No fallback to English. A German build with no German post shows an empty
 * blog, which is honest, rather than an English one under a German URL.
 */
export const blogIndexResolver: ResolveFn<PostMeta[]> = () => {
  const lang = currentLocale();
  return POSTS.filter((post) => post.lang === lang);
};

/**
 * One post, with its body.
 *
 * The body is loaded here rather than in the component because prerendering
 * waits for a resolver and does not wait for a promise started in `ngOnInit`.
 * A body resolved too late is a page that ships with an empty article, which
 * is the one failure that would not be visible until it was indexed.
 */
export const blogPostResolver: ResolveFn<
  ResolvedPost | RedirectCommand
> = async (route) => {
  const router = inject(Router);
  const lang = currentLocale();
  const slug = route.paramMap.get('slug');

  const meta = POSTS.find((post) => post.slug === slug && post.lang === lang);
  const load = meta ? BODIES[`${lang}:${meta.slug}`] : undefined;

  // Only reachable by typing a URL or following a stale link: the prerender
  // routes file for this locale lists exactly the slugs that exist in it.
  if (!meta || !load) {
    return new RedirectCommand(router.parseUrl('/blog'));
  }

  const { html } = await load();
  return { meta, html };
};
